import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { downloadApiFile } from "../utils/download";

function UsersPage() {
  const history = useHistory();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [implementors, setImplementors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchPageData();
  }, []);

  const implementorNameById = useMemo(
    () =>
      implementors.reduce((accumulator, implementor) => {
        accumulator[String(implementor.id)] = implementor.name;
        return accumulator;
      }, {}),
    [implementors]
  );

  const userNameById = useMemo(
    () =>
      users.reduce((accumulator, user) => {
        accumulator[String(user.id)] = `${user.firstname} ${user.lastname}`.trim() || user.username;
        return accumulator;
      }, {}),
    [users]
  );

  async function fetchPageData() {
    setLoading(true);
    setError("");

    try {
      const [usersResponse, rolesResponse, implementorsResponse] = await Promise.all([
        api.get("/users"),
        api.get("/roles"),
        api.get("/implementors")
      ]);

      setUsers(usersResponse.data || []);
      setRoles(rolesResponse.data || []);
      setImplementors(implementorsResponse.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load users page data.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(uuid) {
    try {
      await api.delete(`/users/${uuid}`);
      toast.success("User deleted");
      fetchPageData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete user.");
    }
  }

  async function handleDownload() {
    setDownloading(true);

    try {
      await downloadApiFile("/users/export", {
        fallbackFileName: "users.csv"
      });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download users.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Users"
        action={
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => history.push("/users/new")}>
              Add user
            </Button>
            <Button variant="outline-primary" onClick={handleDownload} disabled={downloading || loading} title="Download users">
              {downloading ? "Downloading..." : "Download"}
            </Button>
          </div>
        }
      />

      <Row className="g-4">
        <Col xs={12}>
          <div className="content-card table-card">
            <h5 className="mb-3">User list</h5>

            {loading ? <LoadingState label="Loading users..." /> : null}
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {!loading && !error && !users.length ? (
              <EmptyState title="No users yet" text="Create the first user from the form on the left." />
            ) : null}

            {!loading && users.length ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Role</th>
                      <th>Email</th>
                      <th>Implementor</th>
                      <th>Designation</th>
                      <th>Supervisor</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.uuid}>
                        <td>
                          <div>{user.firstname} {user.lastname}</div>
                        </td>
                        <td>
                          <small className="text-muted">{user.username}</small>
                        </td>
                        <td>{user.role_name || user.role_id}</td>
                        <td>{user.email}</td>
                        <td>{implementorNameById[String(user.implementor_id || "")] || "-"}</td>
                        <td>{user.designation || "-"}</td>
                        <td>{userNameById[String(user.supervisor_id || "")] || "-"}</td>
                        <td className="text-end">
                          <div className="d-inline-flex gap-2">
                            <Button variant="outline-secondary" size="sm" onClick={() => history.push(`/users/${user.uuid}/edit`)}>
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(user.uuid)}>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default UsersPage;
